import { api } from '@/lib/api';
import type { TranslatorProvider, UpdateSettingsData } from '@fame/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export type SettingsSection = 'registrations' | 'website' | 'system';

export interface Settings {
  id: number;
  updatedAt: string | null;
  articleNumbers?: string[];
  thankYouUrlNl?: string | null;
  thankYouUrlFr?: string | null;
  thankYouUrlEn?: string | null;
  registrationExportDriveFolderId?: string;
  luxuryIntakeRecipients?: string[];
  projectIntakeRecipients?: string[];
  b2bSalesSheetId?: string;
  luxurySalesSheetId?: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpSecure?: boolean;
  smtpUsername?: string;
  smtpPasswordConfigured?: boolean;
  smtpFrom?: string;
  translatorProvider?: TranslatorProvider;
  azureTranslatorKeyConfigured?: boolean;
  azureTranslatorRegion?: string;
  azureTranslatorEndpoint?: string;
  googleTranslatorKeyConfigured?: boolean;
}

export function useSettings(section: SettingsSection) {
  return useQuery({
    queryKey: ['settings', section],
    queryFn: () => api.get<Settings>(`/settings?section=${section}`),
  });
}

export function useUpdateSettings(section: SettingsSection) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSettingsData) => api.patch<Settings>(`/settings?section=${section}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', section] });
      toast.success('Instellingen opgeslagen');
    },
    onError: (error: Error) => {
      toast.error(`Fout bij opslaan instellingen: ${error.message}`);
    },
  });
}

export function useArticleNumbers(): string[] {
  const { data } = useSettings('registrations');
  return data?.articleNumbers ?? [];
}
